import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCopy, Check, FileText, Settings2, Save, Zap, RotateCcw, Clock, MessageCircle, Users } from "lucide-react";
import { apiClient } from "@/App";
import PnjLogo from "@/components/PnjLogo";
import PicInput from "@/components/report/PicInput";
import HandlingCodesDialog from "@/components/report/HandlingCodesDialog";
import TeamsDialog from "@/components/report/TeamsDialog";
import { PIC_ROLES, TEAM_ROLES, CODE_FIELDS, buildReport, findCompletedCode, applyCode, mergeText, codeText } from "@/lib/reportTemplate";

const inputCls =
  "w-full rounded-md bg-[#0b1928] border border-[#243a52] focus:border-[#0ea5e9]/70 focus:outline-none px-3 py-2 text-sm text-white placeholder:text-slate-600 transition-colors";
const labelCls = "block text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-1";

function todayISO() {
  const d = new Date(Date.now() + 7 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

function nowHHMM() {
  return new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(11, 16);
}

function CodeHint() {
  return (
    <span className="text-[10px] text-slate-500 flex items-center gap-1" data-testid="penanganan-hint">
      <Zap className="h-3 w-3 text-[#fbbf24]" /> ketik kode, pilihan akan muncul otomatis
    </span>
  );
}

const EMPTY = { tanggal: "", judul: "", waktu: "", kejadian: "", penanganan: "", penyebab: "", tim: "", pic: {} };

export default function IncidentReport() {
  const [form, setForm] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [codes, setCodes] = useState([]);
  const [picHistory, setPicHistory] = useState({});
  const [codesOpen, setCodesOpen] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const saveTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [li, ph, hc, tm] = await Promise.all([
          apiClient.get("/reports/last-input"),
          apiClient.get("/reports/pic-history"),
          apiClient.get("/reports/handling-codes"),
          apiClient.get("/reports/teams"),
        ]);
        setForm({
          ...EMPTY,
          ...li.data,
          tanggal: li.data.tanggal || todayISO(),
          waktu: /^\d{2}:\d{2}$/.test(li.data.waktu || "") ? li.data.waktu : nowHHMM(),
        });
        setPicHistory(ph.data || {});
        setCodes(hc.data || []);
        setTeams(tm.data || []);
      } catch (e) {
        toast.error("Gagal memuat data laporan");
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const persist = useCallback((next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("dirty");
    saveTimer.current = setTimeout(async () => {
      try {
        await apiClient.put("/reports/last-input", next);
        setSaveState("saved");
      } catch {
        setSaveState("error");
      }
    }, 700);
  }, []);

  const update = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      persist(next);
      return next;
    });
  };
  const updatePic = (role, name) => update({ pic: { ...form.pic, [role]: name } });

  const applyTeam = (name) => {
    const t = teams.find((x) => x.name === name);
    if (!t) {
      update({ tim: "" });
      return;
    }
    const pic = { ...form.pic };
    TEAM_ROLES.forEach((r) => {
      pic[r.key] = t.members?.[r.key] || "";
    });
    update({ tim: name, pic });
    toast.success(`Person in Charge diisi dari Tim ${name}`);
  };

  const resetForm = () => {
    if (!window.confirm("Hapus semua isian form?")) return;
    update({ ...EMPTY, tanggal: todayISO(), waktu: nowHHMM() });
    toast.success("Form direset");
  };

  const commitPic = async (role, name) => {
    const n = (name || "").trim();
    if (!n) return;
    if ((picHistory[role] || [])[0]?.toLowerCase() === n.toLowerCase()) return;
    try {
      const { data } = await apiClient.post("/reports/pic-history", { role, name: n });
      setPicHistory((h) => ({ ...h, [role]: data.names }));
    } catch {
      /* non-critical */
    }
  };

  const fieldRefs = { kejadian: useRef(null), penanganan: useRef(null), penyebab: useRef(null) };

  const applyCodeAt = (c, field, start, end) => {
    const { form: next, caret } = applyCode(form, c, field, start, end);
    update(next);
    toast.success(`Kode ${c.code} diterapkan`);
    requestAnimationFrame(() => {
      const el = fieldRefs[field].current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  // Typing a code followed by space/Enter in any code-aware field fills all three.
  const onCodeField = (field) => (raw) => {
    const hit = findCompletedCode(raw, codes);
    if (hit) {
      const { form: next, caret } = applyCode({ ...form, [field]: raw }, hit.code, field, hit.start, hit.end);
      update(next);
      toast.success(`Kode ${hit.code.code} diterapkan`);
      requestAnimationFrame(() => {
        const el = fieldRefs[field].current;
        if (el) el.setSelectionRange(caret, caret);
      });
      return;
    }
    update({ [field]: raw });
  };

  const insertCode = (c) => {
    const next = { ...form };
    for (const f of CODE_FIELDS) next[f] = mergeText(next[f], codeText(c, f));
    update(next);
    toast.success(`Kode ${c.code} diterapkan`);
  };

  const report = useMemo(() => buildReport(form), [form]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(report);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = report;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Laporan disalin ke clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const sendWhatsApp = () => {
    const a = document.createElement("a");
    a.href = `https://wa.me/?text=${encodeURIComponent(report)}`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("WhatsApp dibuka di tab baru — pilih grup OCC lalu kirim");
  };

  return (
    <div className="min-h-screen bg-[#0c1b2a] text-white relative overflow-hidden" data-testid="incident-report-page">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-25 bg-grid" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(168,85,247,0.12),transparent_55%)]" />
      </div>

      <header className="relative z-10 max-w-7xl mx-auto flex items-center justify-between px-6 pt-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors" data-testid="back-to-menu">
          <ArrowLeft className="h-4 w-4" /> Menu Utama
        </Link>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="manage-teams-btn"
            onClick={() => setTeamsOpen(true)}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-300 hover:text-white px-3 py-2 rounded-md border border-[#243a52] hover:border-[#0ea5e9]/60 transition-colors"
          >
            <Users className="h-3.5 w-3.5" /> Komposisi Tim
          </button>
          <button
            type="button"
            data-testid="manage-codes-btn"
            onClick={() => setCodesOpen(true)}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-300 hover:text-white px-3 py-2 rounded-md border border-[#243a52] hover:border-[#0ea5e9]/60 transition-colors"
          >
            <Settings2 className="h-3.5 w-3.5" /> Kelola Kode
          </button>
          <PnjLogo size={32} />
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-10 pb-16">
        <div className="flex items-center gap-4 mb-8">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-[#a855f7] to-[#0ea5e9] flex items-center justify-center shadow-lg shadow-purple-500/30">
            <FileText className="h-8 w-8 text-[#0c1b2a]" strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-[#c4b5fd]">Modul Aktif</div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold">Pelaporan Gangguan Operasional</h1>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Form */}
          <section className="rounded-2xl border border-[#243a52] bg-gradient-to-br from-[#0d243a]/80 to-[#062036]/80 p-6 space-y-5" data-testid="report-form">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.3em] text-slate-400">Form Laporan</div>
              <div className="flex items-center gap-3">
                <div className="text-[10px] font-mono uppercase tracking-widest flex items-center gap-1 text-slate-500" data-testid="save-state">
                  <Save className="h-3 w-3" />
                  {saveState === "saved" && <span className="text-[#22c55e]">Tersimpan</span>}
                  {saveState === "dirty" && <span>Menyimpan…</span>}
                  {saveState === "error" && <span className="text-red-400">Gagal simpan</span>}
                  {saveState === "idle" && <span>{loaded ? "Auto-save aktif" : "Memuat…"}</span>}
                </div>
                <button
                  type="button"
                  data-testid="reset-form-btn"
                  onClick={resetForm}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1.5 rounded-md border border-[#243a52] text-slate-300 hover:text-red-300 hover:border-red-500/60 transition-colors"
                  title="Hapus semua isian"
                >
                  <RotateCcw className="h-3 w-3" /> Reset
                </button>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-[180px_1fr]">
              <div>
                <label className={labelCls}>Tanggal</label>
                <input data-testid="input-tanggal" type="date" value={form.tanggal} onChange={(e) => update({ tanggal: e.target.value })} className={`${inputCls} font-mono`} />
              </div>
              <PicInput
                role="judul"
                label="Judul Kejadian"
                value={form.judul}
                history={picHistory.judul || []}
                onChange={(v) => update({ judul: v })}
                onCommit={(v) => commitPic("judul", v)}
                placeholder="contoh: Gangguan PSD Stasiun Blok M"
              />
            </div>

            <div>
              <label className={labelCls}>1. Waktu Kejadian</label>
              <div className="flex gap-2">
                <input
                  data-testid="input-waktu"
                  type="time"
                  step={60}
                  value={form.waktu}
                  onChange={(e) => update({ waktu: e.target.value })}
                  className={`${inputCls.replace("w-full", "w-36")} font-mono`}
                />
                <button
                  type="button"
                  data-testid="waktu-now-btn"
                  onClick={() => update({ waktu: nowHHMM() })}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-[#243a52] text-slate-300 hover:text-white hover:border-[#0ea5e9]/60 transition-colors"
                  title="Set ke jam sekarang (WIB)"
                >
                  <Clock className="h-3.5 w-3.5" /> Sekarang
                </button>
                <span className="self-center text-[10px] text-slate-500">WIB · bisa diubah</span>
              </div>
            </div>

            <div>
              <PicInput
                ref={fieldRefs.kejadian}
                role="kejadian"
                label="2. Kejadian dan Dampak"
                multiline
                rows={3}
                value={form.kejadian}
                history={picHistory.kejadian || []}
                codes={codes}
                onPickCode={(c, st, en) => applyCodeAt(c, "kejadian", st, en)}
                onChange={onCodeField("kejadian")}
                onCommit={(v) => commitPic("kejadian", v)}
                placeholder="Uraian kejadian dan dampaknya, atau ketik kode (mis. WRS_ATO)"
                headerRight={<CodeHint />}
              />
              {codes.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="code-chips">
                  {codes.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`code-chip-${c.code}`}
                      onClick={() => insertCode(c)}
                      title={[c.kejadian, c.text, c.penyebab].filter(Boolean).join("\n—\n")}
                      className="text-[10px] font-mono px-2 py-1 rounded bg-[#122032] border border-[#243a52] text-[#7dd3fc] hover:border-[#0ea5e9]/60 hover:bg-[#0ea5e9]/10 transition-colors"
                    >
                      {c.code}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <PicInput
              ref={fieldRefs.penanganan}
              role="penanganan"
              label="3. Penanganan Gangguan"
              multiline
              rows={5}
              value={form.penanganan}
              history={[]}
              codes={codes}
              onPickCode={(c, st, en) => applyCodeAt(c, "penanganan", st, en)}
              onChange={onCodeField("penanganan")}
              placeholder="Ketik kode (mis. WRS_ATO) lalu spasi, atau klik chip kode di bawah"
              headerRight={<CodeHint />}
            />

            <PicInput
              ref={fieldRefs.penyebab}
              role="penyebab"
              label="4. Penyebab Gangguan (dugaan root cause)"
              multiline
              rows={3}
              value={form.penyebab}
              history={picHistory.penyebab || []}
              codes={codes}
              onPickCode={(c, st, en) => applyCodeAt(c, "penyebab", st, en)}
              onChange={onCodeField("penyebab")}
              onCommit={(v) => commitPic("penyebab", v)}
              placeholder="Dugaan penyebab gangguan, atau ketik kode"
              headerRight={<CodeHint />}
            />

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className={`${labelCls} mb-0`}>5. Person in Charge</div>
                <div className="flex items-center gap-2">
                  <Users className="h-3.5 w-3.5 text-[#7dd3fc]" />
                  <select
                    data-testid="select-team"
                    value={form.tim || ""}
                    onChange={(e) => applyTeam(e.target.value)}
                    className="rounded-md bg-[#0b1928] border border-[#243a52] focus:border-[#0ea5e9]/70 focus:outline-none px-2.5 py-1.5 text-xs text-white"
                  >
                    <option value="">Pilih Tim…</option>
                    {teams.map((t) => (
                      <option key={t.name} value={t.name}>
                        Tim {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {PIC_ROLES.map((r) => (
                  <PicInput
                    key={r.key}
                    role={r.key}
                    label={r.label}
                    value={form.pic?.[r.key] || ""}
                    history={picHistory[r.key] || []}
                    onChange={(v) => updatePic(r.key, v)}
                    onCommit={(v) => commitPic(r.key, v)}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Preview */}
          <section className="lg:sticky lg:top-6 self-start rounded-2xl border border-[#243a52] bg-[#0b1928] p-6" data-testid="report-preview-card">
            <div className="flex items-center justify-between mb-3 gap-3">
              <div className="text-xs uppercase tracking-[0.3em] text-slate-400">Preview Laporan</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="copy-report-btn"
                  onClick={copy}
                  className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-md transition-colors ${
                    copied ? "bg-[#22c55e] text-[#0c1b2a]" : "bg-[#0ea5e9] text-[#0c1b2a] hover:bg-[#38bdf8]"
                  }`}
                >
                  {copied ? <Check className="h-4 w-4" /> : <ClipboardCopy className="h-4 w-4" />}
                  {copied ? "Tersalin" : "Copy"}
                </button>
                <button
                  type="button"
                  data-testid="send-whatsapp-btn"
                  onClick={sendWhatsApp}
                  title="Buka WhatsApp dengan teks laporan, lalu pilih grup OCC"
                  className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-md bg-[#25D366] text-[#0c1b2a] hover:bg-[#4ade80] transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  Kirim WhatsApp
                </button>
              </div>
            </div>
            <textarea
              data-testid="report-preview"
              readOnly
              value={report}
              rows={22}
              className="w-full rounded-md bg-[#08131f] border border-[#243a52] px-4 py-3 text-sm text-slate-100 leading-relaxed font-mono resize-y focus:outline-none"
            />
            <p className="mt-2 text-[11px] text-slate-500" data-testid="whatsapp-hint">
              Kirim WhatsApp membuka aplikasi WhatsApp dengan laporan sudah terisi — pilih grup OCC lalu tekan kirim.
            </p>
          </section>
        </div>

        <footer className="mt-16 pt-6 border-t border-[#243a52] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-slate-500">
          <div>
            <span className="text-slate-400 font-display font-semibold">CRLS Console Suite</span> · Pelaporan Gangguan · v1.0
          </div>
          <PnjLogo size={32} />
        </footer>
      </main>

      <HandlingCodesDialog open={codesOpen} onOpenChange={setCodesOpen} codes={codes} onCodesChange={setCodes} />
      <TeamsDialog open={teamsOpen} onOpenChange={setTeamsOpen} teams={teams} onTeamsChange={setTeams} />
    </div>
  );
}
